import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { AuthorizedComponent } from './layout/authorized/authorized.component';
import { RouterModule } from '@angular/router';
import { P404Component } from './pages/p404/p404.component';
import { NavigationBarComponent } from './components/navigation-bar/navigation-bar.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { AppRoutingModule, routes } from './app.routing';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ButtonModule } from 'primeng/button';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { CallbackComponent } from './pages/callback/callback.component';
import { SearchComponent } from './components/search/search.component';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { FormsModule } from '@angular/forms';
import { ActionReducer, ActionReducerMap, MetaReducer, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { localStorageSync, rehydrateApplicationState } from 'ngrx-store-localstorage';
import {
  DataStoreState,
  GlobalConfigStoreModule,
  presentationModeReducer,
  PRESENTATION_MODE_KEY,
  searchReducer,
  SearchStoreModule,
  SEARCH_FEATURE_KEY,
  COUNTRY_TERRITORIES_FEATURE_KEY,
  countryAndTerritoriesReducer,
  userReducer,
  USER_FEATURE_KEY,
  FULLSCREEN_FEATURE_KEY,
  PACKS_NOT_SAVED_CHANGES_FEATURE_KEY,
  fullscreenReducer,
  packsNotSavedChangesReducer,
  PacksNotSavedChangesModule,
  MASTER_TEMPLATE_NOT_SAVED_CHANGES_FEATURE_KEY,
  masterTemplateNotSavedChangesReducer,
  PACK_IN_FOCUS_FEATURE_KEY,
  packInFocusReducer,
  PackInFocusModule,
  COMPARE_PRODUCTS_FEATURE_KEY,
  compareProductsReducer,
  CompareProducts,
  partCostsNotSavedChangesReducer,
  PART_COSTS_NOT_SAVED_CHANGES_FEATURE_KEY,
  PartCostsNotSavedChangesModule,
  GuidelinesNotSavedChangesModule,
  guidelinesNotSavedChangesReducer,
  GUIDELINES_NOT_SAVED_CHANGES_FEATURE_KEY,
} from '@cpak/main-features-lib/data-store';
import { TranslationModule } from './localization/translation.module';
import deepmerge from 'deepmerge';
import { initUser } from '@cpak/main-features-lib/data-store';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { USER_PREFERENCES_KEY, userPreferencesReducer } from '@cpak/main-features-lib/data-store';
import { MenuModule } from 'primeng/menu';
import { GlobalErrorHandler } from './services/global-error-handler.service';
import { P403Component } from './pages/p403/p403.component';
import { ErrorImageModule } from '@cpak/main-features-lib/shared-directives/error-image/error-image.module';
import { CompareProductsBarComponent } from './components/compare-products-bar/compare-products-bar.component';
import { RatingModule } from 'primeng/rating';
import { TrackClickModule } from '@cpak/main-features-lib/shared-directives/track-click/track-click.module';
import { NotificationsPopoverComponent } from './components/notifications-popover/notifications-popover.component';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { DatePipeModule } from '@cpak/main-features-lib/shared-pipes/date/date.module';
import { RippleModule } from 'primeng/ripple';
import { BadgeModule } from 'primeng/badge';
import { TrackingService } from '@cpak/main-features-lib/services';
import { TrackedMenuItemsModule } from '@cpak/main-features-lib/shared-pipes/tracked-menu-items/tracked-menu-items.module';
import { TrackSubmitModule } from '@cpak/main-features-lib/shared-directives/track-submit/track-submit.module';
import { SearchPaksSortType } from '@cpak/adp-cpak-quotes-service-models';
import { SortOrder } from '@cpak/adp-cpak-bff-service-models';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';

const reducers: ActionReducerMap<DataStoreState> = {
  [SEARCH_FEATURE_KEY]: searchReducer,
  [PRESENTATION_MODE_KEY]: presentationModeReducer,
  [COUNTRY_TERRITORIES_FEATURE_KEY]: countryAndTerritoriesReducer,
  [FULLSCREEN_FEATURE_KEY]: fullscreenReducer,
  [USER_FEATURE_KEY]: userReducer,
  [PACKS_NOT_SAVED_CHANGES_FEATURE_KEY]: packsNotSavedChangesReducer,
  [PART_COSTS_NOT_SAVED_CHANGES_FEATURE_KEY]: partCostsNotSavedChangesReducer,
  [GUIDELINES_NOT_SAVED_CHANGES_FEATURE_KEY]: guidelinesNotSavedChangesReducer,
  [USER_PREFERENCES_KEY]: userPreferencesReducer,
  [MASTER_TEMPLATE_NOT_SAVED_CHANGES_FEATURE_KEY]: masterTemplateNotSavedChangesReducer,
  [PACK_IN_FOCUS_FEATURE_KEY]: packInFocusReducer,
  [COMPARE_PRODUCTS_FEATURE_KEY]: compareProductsReducer,
};

export function localStorageSyncReducer(reducer: ActionReducer<DataStoreState>): ActionReducer<DataStoreState> {
  let currentUserId: string | null = null;
  const keysWithNoUserPrefix = [
    USER_FEATURE_KEY,
    PACKS_NOT_SAVED_CHANGES_FEATURE_KEY,
    PART_COSTS_NOT_SAVED_CHANGES_FEATURE_KEY,
    GUIDELINES_NOT_SAVED_CHANGES_FEATURE_KEY,
    PACK_IN_FOCUS_FEATURE_KEY,
  ];
  const storageKeySerializer = (key: string): string =>
    keysWithNoUserPrefix.includes(key) ? key : currentUserId + '-' + key;
  const keys = [
    SEARCH_FEATURE_KEY,
    PRESENTATION_MODE_KEY,
    COUNTRY_TERRITORIES_FEATURE_KEY,
    USER_FEATURE_KEY,
    FULLSCREEN_FEATURE_KEY,
    PACKS_NOT_SAVED_CHANGES_FEATURE_KEY,
    PART_COSTS_NOT_SAVED_CHANGES_FEATURE_KEY,
    GUIDELINES_NOT_SAVED_CHANGES_FEATURE_KEY,
    USER_PREFERENCES_KEY,
    MASTER_TEMPLATE_NOT_SAVED_CHANGES_FEATURE_KEY,
    PACK_IN_FOCUS_FEATURE_KEY,
    COMPARE_PRODUCTS_FEATURE_KEY,
  ];
  return localStorageSync({
    storageKeySerializer,
    syncCondition: state => {
      const userId = state[USER_FEATURE_KEY].userId;
      if (userId) {
        currentUserId = userId;
      }
      return !!currentUserId;
    },
    keys,
    rehydrate: true,
    // eslint-disable-next-line
    mergeReducer: (state: DataStoreState, rehydratedState: any, action: any) => {
      if (action.type === initUser.type) {
        // eslint-disable-next-line
        const overwriteMerge = (destinationArray: any, sourceArray: any) => sourceArray;
        const options: deepmerge.Options = {
          arrayMerge: overwriteMerge,
        };

        currentUserId = action.userId;
        const updatedRehydrateState = rehydrateApplicationState(keys, localStorage, storageKeySerializer, true);
        state = deepmerge(state, updatedRehydrateState, options);

        //SearchPaksSortType.number & SortOrder.asc - old incorrect default sorting value
        if (!state[SEARCH_FEATURE_KEY].paks.incorrectDefaultPackSortCleaned) {
          const packSearchState = state[SEARCH_FEATURE_KEY].paks;
          if (packSearchState.value === SearchPaksSortType.number && packSearchState.order === SortOrder.asc) {
            packSearchState.value = SearchPaksSortType.modifiedDate;
            packSearchState.order = SortOrder.desc;
          }

          packSearchState.incorrectDefaultPackSortCleaned = true;
        }
      }

      //invalid default values in reducer which was saved in some users local storage
      if (state[USER_PREFERENCES_KEY].timeZone === 'US') {
        state[USER_PREFERENCES_KEY].timeZone = '';
      }

      return state;
    },
  })(reducer);
}
const metaReducers: Array<MetaReducer<DataStoreState>> = [localStorageSyncReducer];

@NgModule({
  declarations: [
    AppComponent,
    AuthorizedComponent,
    P404Component,
    P403Component,
    NavigationBarComponent,
    CallbackComponent,
    SearchComponent,
    CompareProductsBarComponent,
    NotificationsPopoverComponent,
  ],
  imports: [
    BrowserModule,
    RouterModule.forRoot(routes),
    AppRoutingModule,
    HttpClientModule,
    TranslationModule,
    BrowserAnimationsModule,
    ButtonModule,
    FormsModule,
    AutoCompleteModule,
    ToastModule,
    SearchStoreModule,
    GlobalConfigStoreModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictActionImmutability: true,
        strictStateImmutability: true,
      },
    }),
    EffectsModule.forRoot([]),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    StoreRouterConnectingModule.forRoot(),
    ToastModule,
    PacksNotSavedChangesModule,
    PartCostsNotSavedChangesModule,
    GuidelinesNotSavedChangesModule,
    PackInFocusModule,
    CompareProducts,
    MenuModule,
    ErrorImageModule,
    RatingModule,
    TrackClickModule,
    OverlayPanelModule,
    DatePipeModule,
    RippleModule,
    BadgeModule,
    TrackedMenuItemsModule,
    TrackSubmitModule,
    ConfirmDialogModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    MessageService,
    TrackingService,
    ConfirmationService,
    DialogService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
